import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../Common/Layout";

// Helper: US state name to abbreviation
const stateNameToAbbr: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  // Add DC and territories if needed
};

interface ProcessingApplicationProps {
  onNext?: (data?: any) => void;
  onPrevious?: () => void;
  workflowData?: any;
  currentStep?: number;
  totalSteps?: number;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  selectedState?: string;
}

interface ProcessingStep {
  id: string;
  title: string;
  description: string;
  status: "pending" | "processing" | "completed" | "error";
  duration?: number;
  startTime?: Date;
  completedTime?: Date;
  errorMessage?: string;
}

export default function ProcessingApplication(props: ProcessingApplicationProps = {}) {
  const { onNext, onPrevious, workflowData, currentStep: propCurrentStep, totalSteps, isFirstStep, isLastStep, selectedState } = props;
  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [transactionId, setTransactionId] = useState<string | null>(() => {
    return searchParams.get("transactionId") || null;
  });

  const handleClearForm = () => {
    // Clear form logic can be implemented here
  };

  const hasNavigatedRef = React.useRef(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [overallStatus, setOverallStatus] = useState<"processing" | "completed" | "error">(() => {
    if (workflowData?.step7?.selectedQuotes && Array.isArray(workflowData.step7.selectedQuotes) && workflowData.step7.selectedQuotes.length > 0) {
      return "completed";
    }
    return "processing";
  });
  const [quotesData, setQuotesData] = useState<any>(() => {
    if (workflowData?.step7?.selectedQuotes && Array.isArray(workflowData.step7.selectedQuotes) && workflowData.step7.selectedQuotes.length > 0) {
      return workflowData.step6.quotes;
    }
    return null;
  });

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState<{ message: string; errors: string[] }>({ message: "", errors: [] });

  const [steps, setSteps] = useState<ProcessingStep[]>(() => {
    const isAlreadyCompleted =
      workflowData?.step7?.selectedQuotes && Array.isArray(workflowData.step7.selectedQuotes) && workflowData.step7.selectedQuotes.length > 0;

    return [
      {
        id: "validation",
        title: "Application Validation",
        description: "Validating policy information and required fields",
        status: isAlreadyCompleted ? "completed" : "pending",
        duration: 3000,
        completedTime: isAlreadyCompleted ? new Date() : undefined,
      },
      {
        id: "turborater",
        title: "Send to TurboRater",
        description: "Submitting application to TurboRater for carrier quotes",
        status: isAlreadyCompleted ? "completed" : "pending",
        duration: 4000,
        completedTime: isAlreadyCompleted ? new Date() : undefined,
      },
      {
        id: "awaiting-quotes",
        title: "Awaiting Quotes",
        description: "Waiting for insurance carrier responses and quote generation",
        status: isAlreadyCompleted ? "completed" : "pending",
        duration: 8000,
        completedTime: isAlreadyCompleted ? new Date() : undefined,
      },
      {
        id: "finalization",
        title: "Processing Complete",
        description: "All quotes received and ready for review",
        status: isAlreadyCompleted ? "completed" : "pending",
        duration: 2000,
        completedTime: isAlreadyCompleted ? new Date() : undefined,
      },
    ];
  });
  useEffect(() => {
    // Skip if navigation has started
    if (hasNavigatedRef.current) {
      return;
    }

    if (workflowData?.step7?.selectedQuotes && Array.isArray(workflowData.step7.selectedQuotes) && workflowData.step7.selectedQuotes.length > 0) {
      setQuotesData(workflowData.step6.quotes);
      setSteps((prev) =>
        prev.map((step) => ({
          ...step,
          status: "completed",
          completedTime: new Date(),
        })),
      );
      setOverallStatus("completed");
      return;
    }

    // Skip if already completed or in error state
    if (overallStatus === "completed" || overallStatus === "error") {
      return;
    }

    let cancelled = false;
    const runStep = async () => {
      // Skip if temporary -1 state (used for retry)
      if (currentStep < 0 || currentStep >= steps.length) return;
      // Don't run if already error
      if (steps[currentStep].status === "error") return;

      // Mark as processing
      setSteps((prev) => {
        const copy = [...prev];
        copy[currentStep] = {
          ...copy[currentStep],
          status: "processing",
          startTime: new Date(),
          errorMessage: undefined,
        };

        // Update description for specific steps
        if (copy[currentStep].id === "turborater") {
          copy[currentStep].description = "Sending to TurboRater...";
        } else if (copy[currentStep].id === "awaiting-quotes") {
          copy[currentStep].description = "Fetching quotes from carriers...";
        }

        return copy;
      });
      await delay(10);

      try {
        if (steps[currentStep].id === "validation") {
          await delay(2000);
        } else if (steps[currentStep].id === "turborater") {
          if (!workflowData?.step6?.quotes || workflowData.step6.quotes.length === 0) {
            await getTurboraterApi();
            await delay(50000);
          }
        } else if (steps[currentStep].id === "awaiting-quotes") {
          await getQuoteDetails();
        } else if (steps[currentStep].id === "finalization") {
          await delay(2000);
        }
        if (cancelled) return;
        setSteps((prev) => {
          const copy = [...prev];
          copy[currentStep] = {
            ...copy[currentStep],
            status: "completed",
            completedTime: new Date(),
          };

          // Update description after successful completion
          if (copy[currentStep].id === "turborater") {
            copy[currentStep].description = "Successfully submitted to TurboRater for carrier quotes";
          } else if (copy[currentStep].id === "awaiting-quotes") {
            copy[currentStep].description = "Quotes received from insurance carriers";
          }

          return copy;
        });
        if (currentStep < steps.length - 1) {
          setCurrentStep((prev) => prev + 1);
        } else {
          setOverallStatus("completed");
        }
      } catch (err) {
        if (cancelled) return;
        let errorMsg = "TurboRater failed. Please retry.";
        let apiErrors: string[] = [];
        if (err && typeof err === "object") {
          if ((err as any).apiErrors) {
            apiErrors = (err as any).apiErrors;
            errorMsg = (err as any).apiMessage || errorMsg;
          } else if (err instanceof Error && err.message) {
            errorMsg = err.message;
          } else if ("message" in err && typeof (err as any).message === "string") {
            errorMsg = (err as any).message;
          }
        }
        setSteps((prev) => {
          const copy = [...prev];
          copy[currentStep] = {
            ...copy[currentStep],
            status: "error",
            errorMessage: errorMsg,
          };
          return copy;
        });
        setOverallStatus("error");

        // Show error modal with details
        if (apiErrors.length > 0 || errorMsg) {
          setErrorModalData({ message: errorMsg, errors: apiErrors });
          setShowErrorModal(true);
        }
      }
    };
    runStep();
    return () => {
      cancelled = true;
    };
  }, [currentStep]);

  const getQuoteDetails = async () => {
    const axios = (await import("axios")).default;
    let attempts = 0;
    const maxAttempts = 50;

    while (attempts < maxAttempts) {
      attempts++;
      try {
        const response = await axios.get(`${process.env.REACT_APP_ACE_API_Url}/api/rate/quotedetails`, {
          params: {
            qid: transactionId,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        });

        if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          setQuotesData(response.data.data);
          return { success: true, data: response.data };
        }

        await delay(1000);
      } catch (error) {
        await delay(1000);
      }
    }

    // If we've exhausted all attempts without getting data, throw an error
    throw new Error("Failed to retrieve quotes after 50 attempts");
  };

  // Helper function to convert "No Coverage" to "None" for API
  const normalizeValueForApi = (value: string | undefined): string => {
    if (!value) return "None";
    return value === "No Coverage" ? "None" : value;
  };

  // Helper function to calculate months licensed based on Year Licensed and Date of Birth
  const calculateMonthsLicensed = (dlYear: string, dateOfBirth: string): number => {
    if (!dlYear) return 0;

    const yearLicensed = parseInt(dlYear);
    if (isNaN(yearLicensed)) return 0;

    // Determine the license date more accurately using date of birth
    let licenseDate: Date;

    if (dateOfBirth) {
      const birthDate = new Date(dateOfBirth);
      // Assume license was obtained on or shortly after their birthday in the year licensed
      licenseDate = new Date(yearLicensed, birthDate.getMonth(), birthDate.getDate());
    } else {
      // Fallback: assume license was obtained on January 1st of the specified year
      licenseDate = new Date(yearLicensed, 0, 1);
    }

    const currentDate = new Date();

    // Calculate total months between license date and current date
    const monthsDiff = (currentDate.getFullYear() - licenseDate.getFullYear()) * 12 + (currentDate.getMonth() - licenseDate.getMonth());

    return Math.max(0, monthsDiff); // Ensure non-negative result
  };
  
  const getTurboraterApi = async () => {
    const axios = (await import("axios")).default;
    const state = selectedState || "";

    const driversInformation = buildDriversInformation(workflowData, stateNameToAbbr[state]);
    const vehicleInformation = buildVehicleInformation(workflowData, stateNameToAbbr[state]);

    const payload = {
      ApplicationId: transactionId,
      PreferedState: stateNameToAbbr[state],

      CustomerInformation: {
        FirstName: workflowData?.step1?.firstName || "",
        LastName: workflowData?.step1?.lastName || "",
        MonthsAtResidence: (workflowData?.step1?.yearsAtResidence || 0) * 12 + (workflowData?.step1?.monthsAtResidence || 0),
        Address: {
          Street1: workflowData?.step1?.address || "",
          City: workflowData?.step1?.city || "",
          State: stateNameToAbbr[workflowData?.step1?.state] || "",
          ZipCode: workflowData?.step1?.zipCode || "",
        },
        ContactInformation: {
          MobilePhone: workflowData?.step1?.cellphone || workflowData?.step1?.homePhone || "",
          EmailAddress: workflowData?.step1?.email || "",
        },

        PriorInsuranceInformation: {
          PriorInsurance: workflowData?.step1?.priorInsurance === "Yes",
          PriorCarrierId: workflowData?.step1?.priorCarrierId || "00000000-0000-0000-0000-000000000000",
          PriorExpirationDate: workflowData?.step1?.priorExpirationDate ? new Date(workflowData.step1.priorExpirationDate).toISOString() : null,
          PriorLiabilityLimit: normalizeValueForApi(workflowData?.step1?.priorLiabilityLimits),
          PriorMonthsCoverage: (Number(workflowData?.step1?.timeWithPriorInsuranceYear) || 0) * 12 + (Number(workflowData?.step1?.timeWithPriorInsuranceMonth) || 0),
          PriorCarrierName: workflowData?.step1?.priorInsuranceCarrier || "",
          ReasonForNoInsurance: workflowData?.step1?.reasonForNoInsurance || "No Reason Given",
          CustomerHasPriorWithThisAgency: workflowData?.step1?.customerHasPriorWithThisAgency === "Yes",
        },
      },

      PolicyInformation: {
        PolicyType: workflowData?.step1?.policyType || "Standard",
        Term: workflowData?.step4?.term || "Two Month",
        EffectiveDate: (() => {
          if (!workflowData?.step4?.policyEffectiveDate) return null;
          const effectiveDate = new Date(workflowData.step4.policyEffectiveDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const toLocalISOString = (d: Date) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}T00:00:00.000Z`;
          };
          return effectiveDate < today ? toLocalISOString(today) : toLocalISOString(effectiveDate);
        })(),
        BumpLimits: "No Bumping",
        PolicyCoverages: getPolicyCoveragesByState(workflowData, stateNameToAbbr[state]),
      },

      DriversInformation: driversInformation,
      VehicleInformation: vehicleInformation,
    };
    try {
      const response = await axios.post(`${process.env.REACT_APP_ACE_API_Url}/api/rate/submit`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        },
      });

      // Check if API returned success: false in the response body
      if (response.data && response.data.success === false) {
        const apiError: any = new Error(response.data.message || "Submission failed");
        apiError.apiErrors = response.data.errors || [];
        apiError.apiMessage = response.data.message || "Submission failed";
        throw apiError;
      }

      return { success: true };
    } catch (error: any) {
      // If it's already our formatted error, re-throw it
      if (error.apiErrors) {
        throw error;
      }
      // Handle axios error responses (4xx, 5xx)
      if (error.response?.data) {
        const respData = error.response.data;
        let parsedErrors: string[] = [];
        // API may return errors as object { "$.Field": ["msg"] } or as array ["msg"]
        if (respData.errors) {
          if (Array.isArray(respData.errors)) {
            parsedErrors = respData.errors;
          } else if (typeof respData.errors === "object") {
            // Convert object format to flat array of readable error strings
            Object.entries(respData.errors).forEach(([field, messages]: [string, any]) => {
              if (Array.isArray(messages)) {
                messages.forEach((msg: string) => parsedErrors.push(`${field}: ${msg}`));
              } else {
                parsedErrors.push(`${field}: ${messages}`);
              }
            });
          }
        }
        const apiError: any = new Error(respData.message || respData.title || "TurboRater failed. Please retry.");
        apiError.apiErrors = parsedErrors;
        apiError.apiMessage = respData.message || respData.title || "TurboRater failed. Please retry.";
        throw apiError;
      }
      throw new Error("TurboRater failed. Please retry.");
    }
  };

  const buildDriversInformation = (workflowData: any, state: string) =>
    (workflowData?.step2 || []).map((driver: any, index: number) => {
      // Calculate months licensed automatically based on dlYear and dateOfBirth
      const monthsLicensed = calculateMonthsLicensed(driver.dlYear, driver.dateOfBirth);
      // Calculate months foreign licensed the same way as months licensed
      const monthsForeignLicense = calculateMonthsLicensed(driver.dlYear, driver.dateOfBirth);

      const base: any = {
        DriverId: driver.id || index + 1,
        FirstName: driver.firstName || "",
        LastName: driver.lastName || "",
        DateOfBirth: driver.dateOfBirth ? new Date(driver.dateOfBirth).toISOString() : null,
        Gender: driver.gender || "",
        MaritalStatus: driver.maritalStatus || "Single",
        DriverType: driver.driverType || "Rated",
        LicenseInformation: {
          LicenseStatus: driver.licenseStatus || "Valid",
          LicenseNumber: driver.dlNumber || undefined,
          MonthsLicensed: monthsLicensed,
          StateLicensed: driver.dlState || "",
          MonthsForeignLicense: monthsForeignLicense,
          CountryOfOrigin: driver.countryOfOrigin || "None",
          InternationalDriversLicense: driver.internationalDriversLicense === "Yes",
        },
        FinancialResponsibilityInformation: {
          Sr22: driver.sr22 === "Yes" ? true : driver.sr22 === "No" ? false : driver.sr22 === true ? true : false,
          Sr22Reason: driver.sr22Reason || "",
          Sr22State: driver.sr22State || "",
          Sr22Date: driver.sr22Date ? new Date(driver.sr22Date).toISOString() : null,
        },

        Attributes: {
          PropertyInsurance: driver.propertyInsurance === "Yes",
          Relation: driver.relationship || "Insured",
          ResidencyStatus: workflowData?.step1?.residenceStatus || "Own",
          ResidencyType: workflowData?.step1?.residenceType || "Home",
          EducationLevel: driver.education || "None",
          MilesToWork: driver.milesToWork || 0,
        },
      };

      // Build Violations array from driver.violations
      {
        let violations: any[] = [];
        if (Array.isArray(driver.violations)) {
          violations = violations.concat(driver.violations);
        }
        if (Array.isArray(driver.Violations)) {
          violations = violations.concat(driver.Violations);
        }
        // Fallback: add from legacy single accidentViolationCode/accidentViolationDate if present
        if (violations.length === 0 && driver.accidentViolationCode && driver.accidentViolationDate) {
          violations.push({
            code: driver.accidentViolationCode,
            OccurredAt: driver.accidentViolationDate,
            OccurredOnSameDayAsAnotherViolation: driver.accidentOccurredOnSameDay ?? false,
          });
        }
        // Map to correct format
        if (violations.length > 0) {
          base.Violations = violations.map((v: any) => ({
            code: v.code || v.Code || "",
            OccurredAt: v.OccurredAt || v.occurredAt || v.Occurred || v.date || "",
            OccurredOnSameDayAsAnotherViolation: v.occurredOnSameDay ?? v.OccurredOnSameDayAsAnotherViolation ?? false,
          }));
        } else {
          base.Violations = [];
        }
      }

      // GA specific
      if (state === "GA") {
        base.MonthsEmployed = 0;
        base.IndustryOccupation = driver.occupation || "Other";
        base.Discounts = {
          AccidentPrevention: false,
          MultiplePolicies: false,
          SeniorDriverCourseDate: null,
          AccidentPreventionCourseDate: null,
        };
      }

      return base;
    });

  const usageValueToLabel: Record<string | number, string> = {
    0: "Artisan Use",
    1: "Business Use",
    4: "Farm",
    7: "Pleasure",
    3: "Work School"
  };
  const buildVehicleInformation = (workflowData: any, state: string) =>
    (workflowData?.step3 || []).map((vehicle: any, index: number) => {
      const usageLabel = usageValueToLabel.hasOwnProperty(vehicle.usage)
        ? usageValueToLabel[vehicle.usage]
        : vehicle.usage || "";
      const base: any = {
        VehicleId: vehicle.id || index + 1,
        Vin: vehicle.vin || "",
        Make: vehicle.make || "",
        Model: vehicle.model || "",
        Year: vehicle.year || "",
        AssignedDriverId: Number(vehicle.assignedDriverId || 1),
        Usage: usageLabel,
        CoverageInformation: {
          CollisionDeductible: normalizeValueForApi(vehicle.collisionDeductible?.toString()),
          ComprehensiveDeductible: normalizeValueForApi(vehicle.comprehensiveDeductible?.toString()),
          RentalLimit: normalizeValueForApi(vehicle.rentalLimit),
          GapCoverage: vehicle.gapLimit === "Yes",
          CustomEquipmentValue: Number(vehicle.customEquipmentLimit || 0),
          SafetyGlassCoverage: vehicle.safetyGlassCoverage === "Yes",
          TowingLimit: normalizeValueForApi(vehicle.towingLaborLimit),
          RoadsideLimit: ["FL", "SC", "TN"].includes(state) ? normalizeValueForApi(vehicle.towingLaborLimit) : "None",
        },
      };

      // States that require mileage
      if (["GA", "TN", "SC"].includes(state)) {
        base.MilesToWork = vehicle.milesToWork || "0";
        base.AnnualMiles = vehicle.annualMileage || "12000";
      }

      // FL / GA purchase info
      if (["FL", "GA"].includes(state)) {
        base.PurchaseType = vehicle.purchaseType || "New";
        base.PurchaseDate = vehicle.purchaseDate ? new Date(vehicle.purchaseDate).toISOString() : null;
      }

      return base;
    });

  const getPolicyCoveragesByState = (workflowData: any, state: string) => {
    const s4 = workflowData?.step4 || {};
    switch (state) {
      case "TN":
        return {
          LiabilityBiLimit: normalizeValueForApi(s4.bodilyInjuryLimit),
          LiabilityPdLimit: normalizeValueForApi(s4.propertyDamageLimit),
          MedPayLimit: normalizeValueForApi(s4.medicalPaymentLimit),
          AccidentalDeathLimit: normalizeValueForApi(s4.accidentalDeathLimit),
          UninsuredMotoristBiLimit: normalizeValueForApi(s4.umbiLimit),
          UninsuredMotoristPdLimit: normalizeValueForApi(s4.uninsuredPdLimit),
        };
      case "OH":
        return {
          LiabilityBiLimit: normalizeValueForApi(s4.bodilyInjuryLimit),
          LiabilityPdLimit: normalizeValueForApi(s4.propertyDamageLimit),
          MedPayLimit: normalizeValueForApi(s4.medicalPaymentLimit),
          AccidentalDeathLimit: normalizeValueForApi(s4.accidentalDeathLimit),
          UnderinsuredMotoristBiLimit: normalizeValueForApi(s4.uimbiLimit),
          UninsuredMotoristBiLimit: normalizeValueForApi(s4.umbiLimit),
          UninsuredMotoristPdLimit: normalizeValueForApi(s4.uninsuredPdLimit),
        };

      case "FL":
        return {
          LiabilityBiLimit: normalizeValueForApi(s4.bodilyInjuryLimit),
          LiabilityPdLimit: normalizeValueForApi(s4.propertyDamageLimit),
          MedPayLimit: normalizeValueForApi(s4.medicalPaymentLimit),
          PipDeductible: normalizeValueForApi(s4.pipDed) === "None" ? "0" : normalizeValueForApi(s4.pipDed),
          PipDeductibleOption: normalizeValueForApi(s4.pipDedOption) === "None" ? "Named Insured" : normalizeValueForApi(s4.pipDedOption),
          PipType: normalizeValueForApi(s4.pipType) === "None" ? "Basic" : normalizeValueForApi(s4.pipType),
          UninsuredMotoristBiLimit: normalizeValueForApi(s4.umbiLimit),
          StackedUninsuredMotorist: s4.umStackedMedicalPayment === "Yes",
          WageLossExclusion: s4.wageLossExclusion === "Yes",
          AccidentalDeathLimit: normalizeValueForApi(s4.accidentalDeathLimit),
        };

      case "GA":
        return {
          LiabilityBiLimit: normalizeValueForApi(s4.bodilyInjuryLimit),
          LiabilityPdLimit: normalizeValueForApi(s4.propertyDamageLimit),
          MedPayLimit: normalizeValueForApi(s4.medicalPaymentLimit),
          UninsuredType: normalizeValueForApi(s4.uninsuredType) === "None" ? "None" : normalizeValueForApi(s4.uninsuredType),
          UninsuredMotoristBiLimit: normalizeValueForApi(s4.umbiLimit),
          UninsuredMotoristPdLimit: normalizeValueForApi(s4.uninsuredPdLimit),
          // Send Uninsured PD Deductible for Georgia only
          UninsuredMotoristPdDed: normalizeValueForApi(s4.uninsuredPdDeductible),
          AccidentalDeathLimit: normalizeValueForApi(s4.accidentalDeathLimit),
        };

      case "SC":
        return {
          LiabilityBiLimit: normalizeValueForApi(s4.bodilyInjuryLimit),
          LiabilityPdLimit: normalizeValueForApi(s4.propertyDamageLimit),
          MedPayLimit: normalizeValueForApi(s4.medicalPaymentLimit),
          UnderinsuredMotoristBiLimit: normalizeValueForApi(s4.uimbiLimit),
          // Send Underinsured PD for South Carolina only
          UnderinsuredMotoristPdLimit: normalizeValueForApi(s4.underinsuredPd),
          UninsuredMotoristBiLimit: normalizeValueForApi(s4.umbiLimit),
          UninsuredMotoristPdLimit: normalizeValueForApi(s4.uninsuredPdLimit),
          AccidentalDeathLimit: normalizeValueForApi(s4.accidentalDeathLimit),
        };
      case "AL":
        return {
          LiabilityBiLimit: normalizeValueForApi(s4.bodilyInjuryLimit),
          LiabilityPdLimit: normalizeValueForApi(s4.propertyDamageLimit),
          MedPayLimit: normalizeValueForApi(s4.medicalPaymentLimit),
          UninsuredMotoristBiLimit: normalizeValueForApi(s4.umbiLimit),
          AccidentalDeathLimit: normalizeValueForApi(s4.accidentalDeathLimit),
        };
      default:
        return {
          AccidentalDeathLimit: normalizeValueForApi(s4.accidentalDeathLimit),
          LiabilityBiLimit: normalizeValueForApi(s4.bodilyInjuryLimit),
          LiabilityPdLimit: normalizeValueForApi(s4.propertyDamageLimit),
          UnderinsuredMotoristBiLimit: normalizeValueForApi(s4.uimbiLimit),
          UninsuredMotoristBiLimit: normalizeValueForApi(s4.umbiLimit),
          MedPayLimit: normalizeValueForApi(s4.medicalPaymentLimit),
          UninsuredMotoristPdLimit: normalizeValueForApi(s4.uninsuredPdLimit),
        };
    }
  };

  // const handleRetry = (stepIndex: number) => {
  //   setSteps(prevSteps => {
  //     const newSteps = [...prevSteps];
  //     newSteps[stepIndex].status = 'pending';
  //     newSteps[stepIndex].errorMessage = undefined;
  //     return newSteps;
  //   });

  //   setCurrentStep(stepIndex);
  //   setOverallStatus('processing');
  // };
  const handleRetry = async (stepIndex: number) => {
    const stepToRetry = steps[stepIndex];
    setSteps((prev) => {
      const copy = [...prev];
      copy[stepIndex].status = "pending";
      copy[stepIndex].errorMessage = undefined;
      return copy;
    });
    setOverallStatus("processing");

    // Force re-run by setting to -1 first, then to the target step
    // This ensures the useEffect triggers even if we're retrying the current step
    if (currentStep === stepIndex) {
      setCurrentStep(-1);
      setTimeout(() => setCurrentStep(stepIndex), 0);
    } else {
      setCurrentStep(stepIndex);
    }
  };

  const handleViewQuotes = () => {
    hasNavigatedRef.current = true;
    if (onNext) {
      onNext({ quotes: quotesData });
    }
  };

  const handleContinue = () => {
    hasNavigatedRef.current = true;
    if (onNext) {
      onNext({ quotes: quotesData });
    }
  };

  const getStepIcon = (step: ProcessingStep, index: number) => {
    switch (step.status) {
      case "completed":
        return (
          <div className="step-icon completed">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case "processing":
        return (
          <div className="step-icon processing">
            <div className="spinner"></div>
          </div>
        );
      case "error":
        return (
          <div className="step-icon error">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="step-icon pending">
            <span>{index + 1}</span>
          </div>
        );
    }
  };

  const getProgressPercentage = () => {
    const completedSteps = steps.filter((step) => step.status === "completed").length;
    return (completedSteps / steps.length) * 100;
  };

  const getEstimatedTime = () => {
    const remainingSteps = steps.slice(currentStep);
    const totalTime = remainingSteps.reduce((sum, step) => sum + (step.duration || 0), 0);
    return Math.ceil(totalTime / 1000);
  };

  const mainJSX = (
    <div className="page-container">
      <div className="page-header">
        <h1>Processing Application</h1>
        <p>Your insurance application is being processed through our carrier network</p>
      </div>

      <div className="processing-container">
        {/* Overall Progress */}
        <div className="progress-overview">
          <div className="progress-header">
            <div className="progress-info">
              <h3>Processing Status</h3>
              <p className={`status-text ${overallStatus}`}>
                {overallStatus === "processing" && "In Progress"}
                {overallStatus === "completed" && "Completed Successfully"}
                {overallStatus === "error" && "Action Required"}
              </p>
            </div>
            <div className="progress-stats">
              <div className="stat">
                <span className="stat-value">{Math.round(getProgressPercentage())}%</span>
                <span className="stat-label">Complete</span>
              </div>
              {overallStatus === "processing" && (
                <div className="stat">
                  <span className="stat-value">{getEstimatedTime()}s</span>
                  <span className="stat-label">Remaining</span>
                </div>
              )}
            </div>
          </div>

          <div className="progress-bar-container">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${getProgressPercentage()}%` }}></div>
            </div>
          </div>
        </div>

        {/* Processing Steps */}
        <div className="processing-steps">
          {steps.map((step, index) => (
            <div key={step.id} className={`processing-step ${step.status}`}>
              <div className="step-content">
                <div className="step-left">
                  {getStepIcon(step, index)}
                  <div className="step-details">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>

                    {step.status === "processing" && (
                      <div className="step-progress">
                        <div className="processing-dots">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                        <span className="processing-text">Processing...</span>
                      </div>
                    )}

                    {step.status === "completed" && step.completedTime && (
                      <div className="step-timing">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                        <span>Completed at {step.completedTime.toLocaleTimeString()}</span>
                      </div>
                    )}

                    {step.status === "error" && (
                      <div className="step-error">
                        <div className="error-message">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                            />
                          </svg>
                          <span>{step.errorMessage}</span>
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button className="retry-button" onClick={() => handleRetry(index)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                              />
                            </svg>
                            Retry
                          </button>
                          {errorModalData.errors.length > 0 && (
                            <button
                              className="retry-button"
                              onClick={() => setShowErrorModal(true)}
                              style={{ backgroundColor: "#FEE2E2", color: "#DC2626", borderColor: "#FECACA" }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                width="16"
                                height="16"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                                />
                              </svg>
                              View Errors
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="step-right">
                  {step.status === "processing" && (
                    <div className="step-animation">
                      <div className="pulse-ring"></div>
                      <div className="pulse-ring delay-1"></div>
                      <div className="pulse-ring delay-2"></div>
                    </div>
                  )}

                  {step.status === "completed" && (
                    <div className="step-success">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {index < steps.length - 1 && <div className={`step-connector ${step.status === "completed" ? "completed" : ""}`}></div>}
            </div>
          ))}
        </div>

        {/* Completion Actions - Only show in standalone mode */}
        {overallStatus === "completed" && !(onNext && onPrevious) && (
          <div className="completion-section">
            <div className="completion-content">
              <div className="completion-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
                  />
                </svg>
              </div>
              <div className="completion-text">
                <h3>Processing Complete!</h3>
                <p>Your application has been successfully processed and quotes are ready for review.</p>
              </div>
            </div>

            <div className="completion-actions">
              <button className="btn btn-secondary">Download Summary</button>
              <button className="btn-primary" onClick={handleViewQuotes}>
                View Quotes
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* System Information */}
        <div className="system-info">
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.348 14.652a3.75 3.75 0 0 1 0-5.304m5.304 0a3.75 3.75 0 0 1 0 5.304m-7.425 2.121a6.75 6.75 0 0 1 0-9.546m9.546 0a6.75 6.75 0 0 1 0 9.546M5.106 18.894c3.808-3.807 9.98-3.807 13.788 0M12 12h.008v.008H12V12Z"
                  />
                </svg>
              </div>
              <div className="info-content">
                <h5>Momentum AMS</h5>
                <p>Connected</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
                </svg>
              </div>
              <div className="info-content">
                <h5>TurboRater</h5>
                <p>Active</p>
              </div>
            </div>

            <div className="info-item">
              <div className="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3s-4.5 4.03-4.5 9 2.015 9 4.5 9Z"
                  />
                </svg>
              </div>
              <div className="info-content">
                <h5>Carrier Network</h5>
                <p>12 Carriers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {showErrorModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "520px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  backgroundColor: "#FEE2E2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#DC2626" width="24" height="24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                  />
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: "18px", color: "#991B1B" }}>Submission Failed</h3>
            </div>

            <p style={{ margin: "0 0 16px", color: "#374151", fontSize: "14px" }}>{errorModalData.message}</p>

            {errorModalData.errors.length > 0 && (
              <div
                style={{
                  backgroundColor: "#FEF2F2",
                  border: "1px solid #FECACA",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "24px",
                }}
              >
                <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: "13px", color: "#991B1B" }}>Errors:</p>
                <ul style={{ margin: 0, paddingLeft: "20px", listStyle: "disc" }}>
                  {errorModalData.errors.map((err, i) => (
                    <li key={i} style={{ color: "#B91C1C", fontSize: "13px", marginBottom: "4px" }}>
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                onClick={() => setShowErrorModal(false)}
                style={{
                  padding: "8px 20px",
                  borderRadius: "6px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#374151",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workflow Navigation - Only show in stepper mode */}
      {onNext && onPrevious && (
        <div className="form-actions">
          {!isFirstStep && (
            <button className="btn btn-secondary" onClick={onPrevious}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
              </svg>
              Previous
            </button>
          )}
          {/* <button type="button" className="btn btn-secondary" onClick={handleClearForm}>
             
              Clear Form
            </button> */}
          <button
            className="btn btn-primary"
            onClick={handleContinue}
            disabled={overallStatus !== "completed"}
            style={{
              opacity: overallStatus !== "completed" ? 0.5 : 1,
              cursor: overallStatus !== "completed" ? "not-allowed" : "pointer",
            }}
          >
            {overallStatus === "processing" ? "Processing..." : "Continue"}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );

  // Return without Layout if being used in a stepper workflow
  if (onNext || propCurrentStep !== undefined) {
    return mainJSX;
  }

  // Return wrapped in Layout for standalone page
  return <Layout currentPage="Processing Application">{mainJSX}</Layout>;
}
